/**
 * 학습 일정 캘린더 공용 상수·순수 헬퍼 (JCG-2310).
 *
 * 월(MonthGrid)·주(WeekBoard)·카드(TaskCard)·게스트 목업이 공유하는 색 체계·날짜 포맷·분류를
 * 한 곳에 모은다. 색은 디자인 토큰 + amber 액센트(공통)와 계획 계열색(이론=blue, 코딩=green)을
 * Phase 3/4(DashboardTodaySchedule·StudyPlanOnTrackCard)와 **동일 체계**로 유지한다.
 * ui-patterns dark-mode-colors §2/§3 의 light↔dark 페어 규칙을 따른다.
 */
import type { StudyPlanId, StudyTaskKind } from "@/lib/study-plan";
import type { ScheduledTask } from "@/lib/study-plan-state";

/** 계획 라벨 칩(이론=blue, 코딩=green). Phase 3/4 와 동일. */
export const PLAN_CHIP: Record<StudyPlanId, string> = {
  "theory-4week":
    "bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300",
  "coding-6week":
    "bg-green-50 text-green-700 dark:bg-green-950/50 dark:text-green-300",
  "warrior-13day": "bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
};

/** 계획 색 점/스트라이프(월 그리드 점·주 카드 좌측 스트라이프). 채움(done) 버전. */
export const PLAN_DOT_SOLID: Record<StudyPlanId, string> = {
  "theory-4week": "bg-blue-500 dark:bg-blue-400",
  "coding-6week": "bg-green-500 dark:bg-green-400",
  "warrior-13day": "bg-amber-500 dark:bg-amber-400",
};

/** 계획 색 점 — 미완(빈 점, 테두리만). */
export const PLAN_DOT_HOLLOW: Record<StudyPlanId, string> = {
  "theory-4week": "border-blue-500 dark:border-blue-400",
  "coding-6week": "border-green-500 dark:border-green-400",
  "warrior-13day": "border-amber-500 dark:border-amber-400",
};

/** 주 카드 좌측 plan 색 스트라이프. */
export const PLAN_STRIPE: Record<StudyPlanId, string> = {
  "theory-4week": "bg-blue-400 dark:bg-blue-500",
  "coding-6week": "bg-green-400 dark:bg-green-500",
  "warrior-13day": "bg-amber-400 dark:bg-amber-500",
};

/** 범례 점(헤더). */
export const PLAN_LEGEND_DOT: Record<StudyPlanId, string> = {
  "theory-4week": "bg-blue-500 dark:bg-blue-400",
  "coding-6week": "bg-green-500 dark:bg-green-400",
  "warrior-13day": "bg-amber-500 dark:bg-amber-400",
};

/** 계획 시작 pill — plan 칩과 동일 계열(시작 배너가 칩들과 어우러지게). DashboardTodaySchedule 와 동일. */
export const PLAN_START_PILL: Record<StudyPlanId, string> = {
  "theory-4week":
    "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950/50 dark:text-blue-300 dark:hover:bg-blue-900/40",
  "coding-6week":
    "border-green-200 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-900 dark:bg-green-950/50 dark:text-green-300 dark:hover:bg-green-900/40",
  "warrior-13day": "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-300 dark:hover:bg-amber-900/40",
};

/** 감자시험 계열 — 셀 배경 옅은 amber 틴트로 "시험 구간" 표시. */
export const EXAM_KINDS: ReadonlySet<StudyTaskKind> = new Set<StudyTaskKind>([
  "gamja",
  "king-gamja",
  "random-quiz",
  "mock-exam",
]);

export function isExamKind(kind: StudyTaskKind): boolean {
  return EXAM_KINDS.has(kind);
}

/** kind 기본 이모지(task.emoji 가 비었을 때 폴백). 데이터는 보통 emoji 를 제공한다. */
const KIND_EMOJI: Record<StudyTaskKind, string> = {
  theory: "📘",
  coding: "💻",
  problem: "✏️",
  gamja: "🥔",
  "king-gamja": "👑",
  "random-quiz": "🎲",
  "mock-exam": "📝",
};

export function taskEmoji(task: Pick<ScheduledTask, "emoji" | "kind">): string {
  return task.emoji || KIND_EMOJI[task.kind] || "📘";
}

/** 다국어 코딩 페이지 task 의 언어 라벨(빈 문자열이면 언어 무관 task, JCG-2658). */
const CODE_LANG_LABEL: Record<string, string> = {
  c: "C",
  java: "Java",
  python: "Python",
};

/** task.codeLang → 표시 라벨(C/Java/Python). 없으면 "". */
export function codeLangLabel(codeLang: string): string {
  return codeLang ? CODE_LANG_LABEL[codeLang] ?? codeLang.toUpperCase() : "";
}

/**
 * 언어 뱃지(C/Java/Python) 공용 스타일 — 중립 톤(JCG-2658).
 *
 * 이 화면은 계획색(이론=blue·코딩=green)과 amber 액센트(완료/오늘/체크박스)를 이미 쓰므로, 언어
 * 뱃지는 계획색·amber 와 겹치지 않도록 **muted/border 디자인 토큰**(OKLCH, light↔dark 자동 전환)만
 * 쓴다. 언어 구분은 라벨 텍스트(C/Java/Python)로 충분하므로 별도 의미 색상을 주지 않는다.
 * `shrink-0`·`whitespace-nowrap` 로 좁은 칼럼(150~190px)·가로 스크롤 pill 에서 줄바꿈/찌그러짐 방지.
 *
 * (월 그리드 셀처럼 극도로 좁은 곳은 이 뱃지 대신 굵은 텍스트 프리픽스를 쓴다 — MonthGrid `PlanBar` 참고.)
 *
 * 텍스트는 `text-foreground`(near-black 토큰). muted-foreground(≈4.35:1) 는 10px 작은 글씨 AA(4.5:1)에
 * 근소하게 미달해, 3개 동일 제목을 구분하는 정보성 뱃지에는 대비를 확보한 foreground 를 쓴다(hue 없음 → 중립 유지).
 */
export const CODE_LANG_BADGE =
  "inline-flex shrink-0 items-center whitespace-nowrap rounded border border-border bg-muted px-1 text-[10px] font-semibold leading-tight text-foreground";

export const WEEKDAY_KO = ["일", "월", "화", "수", "목", "금", "토"] as const;

/**
 * 주(week) 뷰 슬라이딩 윈도우 폭(일). 고정 7일(캘린더 주) 대신 임의 시작일부터 5일만 보여
 * 칼럼 폭을 넓혀 제목 가독성을 확보한다(JCG-2310). 스와이프/화살표로 하루~여러 날 이동.
 */
export const WINDOW_DAYS = 5;

/** UTC 자정 기준 Date(타임존 드리프트 없이 날짜만 다룸). */
export function dateFromISO(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

// 'M/D' 포맷은 대시보드 토스트(usePlanDynamicState)도 쓰므로 lib 에 두고 여기서 재노출한다.
export { formatMonthDay } from "@/lib/study-plan";

/** 'YYYY-MM-DD' → 'M/D(요일)' */
export function formatMonthDayWeekday(iso: string): string {
  const d = dateFromISO(iso);
  const wd = WEEKDAY_KO[d.getUTCDay()];
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}(${wd})`;
}

/** 'YYYY-MM-DD' → 'YYYY년 M월' */
export function formatYearMonth(iso: string): string {
  const d = dateFromISO(iso);
  return `${d.getUTCFullYear()}년 ${d.getUTCMonth() + 1}월`;
}

/** 해당 ISO 날짜의 요일(0=일). */
export function weekdayOf(iso: string): number {
  return dateFromISO(iso).getUTCDay();
}

/** 그 주의 일요일(주 시작) ISO. (월 그리드 셀 계산 등에서만 사용 — 5일 윈도우는 스냅 안 함) */
export function startOfWeekISO(iso: string): string {
  const d = dateFromISO(iso);
  d.setUTCDate(d.getUTCDate() - d.getUTCDay());
  return d.toISOString().slice(0, 10);
}

/** ISO 날짜를 [loISO, hiISO] 범위로 clamp(문자열 사전순 = 날짜순이라 직접 비교 가능). */
export function clampISO(iso: string, loISO: string, hiISO: string): string {
  if (iso < loISO) return loISO;
  if (iso > hiISO) return hiISO;
  return iso;
}

/** 그 달 1일이 속한 주의 일요일 ISO(6주 월 그리드 시작점). */
export function startOfMonthGridISO(iso: string): string {
  const d = dateFromISO(iso);
  d.setUTCDate(1);
  d.setUTCDate(1 - d.getUTCDay());
  return d.toISOString().slice(0, 10);
}

/** 'YYYY-MM-DD' 의 월(1-based). */
export function monthOf(iso: string): number {
  return dateFromISO(iso).getUTCMonth() + 1;
}

/** 같은 달인지(YYYY-MM 비교). */
export function isSameMonth(aISO: string, bISO: string): boolean {
  return aISO.slice(0, 7) === bISO.slice(0, 7);
}
